import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, FileSpreadsheet, ImagePlus, Upload, X } from "lucide-react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BulkCreateListings as submitBulkCreate,
  FetchBulkCreateOptions,
  ValidateBulkCreateListings,
} from "../../apis/Property";

// Excel shows English labels; import maps them back to these keys for the API.
// Column order follows clientside /listing/create (multi-unit → … → availability),
// then post-create hosting fields (Wi-Fi, security deposit).
const LISTING_FIELDS = [
  { key: "mobileNumber", label: "Mobile number" },
  { key: "userId", label: "Account ID (optional)" },
  // multi-unit
  { key: "totalUnits", label: "Total units" },
  // room / types
  { key: "propertyType", label: "Property type" },
  { key: "roomType", label: "Room type" },
  { key: "roomAmount", label: "Room amount" },
  { key: "personalHome", label: "Personal home" },
  // bedrooms / capacity
  { key: "guests", label: "Guests" },
  { key: "bedrooms", label: "Bedrooms" },
  { key: "bedsTotal", label: "Total beds" },
  { key: "bedTypes", label: "Bed types" },
  { key: "bathroomsTotal", label: "Total bathrooms" },
  { key: "bathroomTypes", label: "Bathroom types" },
  // location
  { key: "countryEn", label: "Country (English)" },
  { key: "countryAr", label: "Country (Arabic)" },
  { key: "street", label: "Street" },
  { key: "apt", label: "Apartment / unit" },
  { key: "cityEn", label: "City (English)" },
  { key: "cityAr", label: "City (Arabic)" },
  { key: "state", label: "District / state" },
  { key: "zip", label: "ZIP / postal code" },
  // map
  { key: "latitudes", label: "Latitude" },
  { key: "longitudes", label: "Longitude" },
  // amenities
  { key: "essentials", label: "Essentials" },
  { key: "safeties", label: "Safety amenities" },
  // spaces
  { key: "spaces", label: "Spaces" },
  // description
  { key: "title", label: "Title" },
  { key: "description", label: "Description" },
  // house rules
  { key: "propertyRules", label: "Property rules" },
  // advance notice / check-in
  { key: "checkInFrom", label: "Check-in from" },
  { key: "checkInTo", label: "Check-in to" },
  { key: "cancellationPolicy", label: "Cancellation policy" },
  // pricing
  { key: "currency", label: "Currency" },
  { key: "basePrice", label: "Base price" },
  { key: "taxes", label: "Taxes (%)" },
  { key: "thursdayPrice", label: "Thursday price" },
  { key: "fridayPrice", label: "Friday price" },
  { key: "saturdayPrice", label: "Saturday price" },
  // discounts
  { key: "weeklyDiscount", label: "Weekly discount (%)" },
  { key: "monthlyDiscount", label: "Monthly discount (%)" },
  // availability
  { key: "availabilityWindow", label: "Availability window" },
  { key: "minNights", label: "Minimum nights" },
  { key: "maxNights", label: "Maximum nights" },
  // hosting settings (after create flow)
  { key: "wifiName", label: "Wi-Fi name" },
  { key: "wifiPassword", label: "Wi-Fi password" },
  { key: "securityDepositEnabled", label: "Security deposit enabled" },
  { key: "securityDepositAmount", label: "Security deposit amount" },
  { key: "securityDepositRefundMethod", label: "Security deposit refund method" },
  { key: "securityDepositNotes", label: "Security deposit notes" },
];

const LISTING_FIELD_KEYS = LISTING_FIELDS.map((field) => field.key);
const LABEL_BY_KEY = Object.fromEntries(
  LISTING_FIELDS.map((field) => [field.key, field.label]),
);
const KEY_BY_HEADER = Object.fromEntries([
  ...LISTING_FIELDS.map((field) => [field.label.toLowerCase(), field.key]),
  // Accept legacy machine headers from older example workbooks.
  ...LISTING_FIELDS.map((field) => [field.key.toLowerCase(), field.key]),
]);

const REQUIRED_LISTING_KEYS = LISTING_FIELD_KEYS.filter(
  (column) => column !== "userId",
);

const CATALOG_COLUMNS = [
  ["propertyTypes", "propertyType"],
  ["roomTypes", "roomType"],
  ["bedTypes", "bedTypes"],
  ["bathroomTypes", "bathroomTypes"],
  ["essentials", "essentials"],
  ["safeties", "safeties"],
  ["spaces", "spaces"],
  ["propertyRules", "propertyRules"],
  ["cancellationPolicies", "cancellationPolicy"],
];

const MIN_PHOTOS = 3;
const MAX_PHOTOS = 15;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const MAX_PHOTO_DIMENSION = 5000;
const ALLOWED_PHOTO_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const PHOTO_REQUIREMENTS_EN =
  "File requirements: each photo can be up to 5 MB with a maximum resolution of 5000×5000 pixels. You need at least 3 photos per listing (up to 15).";

const cellValue = (value) => {
  if (value && typeof value === "object") {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text).join("");
    }
    if (value.text !== undefined) return value.text;
    if (value.result !== undefined) return value.result;
    return "";
  }
  return value ?? "";
};

const readImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const dimensions = { width: image.naturalWidth, height: image.naturalHeight };
      URL.revokeObjectURL(objectUrl);
      resolve(dimensions);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Could not read image dimensions for ${file.name}`));
    };
    image.src = objectUrl;
  });

const isEmptyRow = (row) =>
  LISTING_FIELD_KEYS.every((column) => {
    const value = row[column];
    return value === undefined || value === null || String(value).trim() === "";
  });

const countSemicolonList = (value) =>
  String(value ?? "")
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean).length;

const getLinkedTypeCountErrors = (listingsToCheck) => {
  const errors = [];
  listingsToCheck.forEach((listing) => {
    const bedsTotal = Number(listing.bedsTotal);
    const bathroomsTotal = Number(listing.bathroomsTotal);
    const bedTypeCount = countSemicolonList(listing.bedTypes);
    const bathroomTypeCount = countSemicolonList(listing.bathroomTypes);

    if (Number.isInteger(bedsTotal) && bedTypeCount !== bedsTotal) {
      errors.push({
        rowNumber: listing.rowNumber,
        field: "bedTypes",
        message: `Bed types must list exactly one type per bed (expected ${bedsTotal}, got ${bedTypeCount}). Use semicolon-separated values, e.g. Queen;Queen;Single`,
      });
    }
    if (
      Number.isInteger(bathroomsTotal) &&
      bathroomTypeCount !== bathroomsTotal
    ) {
      errors.push({
        rowNumber: listing.rowNumber,
        field: "bathroomTypes",
        message: `Bathroom types must list exactly one type per bathroom (expected ${bathroomsTotal}, got ${bathroomTypeCount}). Use semicolon-separated values, e.g. Private;Shared`,
      });
    }
  });
  return errors;
};

const revokePhotoUrls = (photosByRow) => {
  Object.values(photosByRow || {}).forEach((entries) => {
    (entries || []).forEach((entry) => {
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    });
  });
};

const BulkCreateListings = () => {
  const fileInputRef = useRef(null);
  const photoInputRefs = useRef({});
  const photosByRowRef = useRef({});
  const [catalogs, setCatalogs] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const [isDownloadingExample, setIsDownloadingExample] = useState(false);
  const [fileName, setFileName] = useState("");
  const [listings, setListings] = useState([]);
  const [photosByRow, setPhotosByRow] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [createdListings, setCreatedListings] = useState([]);
  const [parseError, setParseError] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [isValidatingWorkbook, setIsValidatingWorkbook] = useState(false);
  const [workbookValid, setWorkbookValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  photosByRowRef.current = photosByRow;

  useEffect(() => {
    let isCurrent = true;
    FetchBulkCreateOptions()
      .then((response) => {
        if (!response.success || !response.catalogs) {
          throw new Error("Could not load enabled listing options.");
        }
        if (isCurrent) setCatalogs(response.catalogs);
      })
      .catch((error) => {
        if (isCurrent) {
          setCatalogError(
            error.response?.data?.message ||
              error.message ||
              "Could not load enabled listing options.",
          );
        }
      });
    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    return () => revokePhotoUrls(photosByRowRef.current);
  }, []);

  const allPhotosReady = useMemo(() => {
    if (!listings.length || !workbookValid) return false;
    return listings.every((listing) => {
      const count = photosByRow[listing.rowNumber]?.length || 0;
      return count >= MIN_PHOTOS && count <= MAX_PHOTOS;
    });
  }, [listings, photosByRow, workbookValid]);

  const validateWorkbookRows = async (rows) => {
    setIsValidatingWorkbook(true);
    setValidationErrors([]);
    setWorkbookValid(false);
    try {
      const localErrors = getLinkedTypeCountErrors(rows);
      if (localErrors.length) {
        setValidationErrors(localErrors);
        toast.error(
          "Workbook has errors. Fix the Excel file and upload it again before adding photos.",
        );
        return false;
      }

      const response = await ValidateBulkCreateListings(rows);
      if (!response.success) {
        setValidationErrors(response.errors || []);
        toast.error(
          "Workbook has errors. Fix the Excel file and upload it again before adding photos.",
        );
        return false;
      }

      setWorkbookValid(true);
      toast.success("Workbook looks valid. You can attach photos next.");
      return true;
    } catch (error) {
      const response = error.response?.data;
      if (Array.isArray(response?.errors)) {
        setValidationErrors(response.errors);
        toast.error(
          "Workbook has errors. Fix the Excel file and upload it again before adding photos.",
        );
      } else {
        setParseError(
          response?.message ||
            "Could not validate this workbook. Please try again.",
        );
      }
      return false;
    } finally {
      setIsValidatingWorkbook(false);
    }
  };

  const replacePhotosByRow = (next) => {
    revokePhotoUrls(photosByRowRef.current);
    setPhotosByRow(next);
  };

  const downloadExample = async () => {
    if (!catalogs || isDownloadingExample) return;
    setIsDownloadingExample(true);
    setParseError("");

    try {
      const requiredCatalogs = [
        "propertyTypes",
        "roomTypes",
        "bedTypes",
        "bathroomTypes",
        "essentials",
        "safeties",
        "spaces",
        "cancellationPolicies",
      ];
      const missingCatalogs = requiredCatalogs.filter(
        (key) => !catalogs[key]?.length,
      );
      if (missingCatalogs.length) {
        throw new Error(
          `Cannot build a ready-to-edit example because these enabled option lists are empty: ${missingCatalogs.join(", ")}.`,
        );
      }

      const ExcelJS = await import("exceljs");
      const Workbook = ExcelJS.Workbook || ExcelJS.default?.Workbook;
      if (!Workbook) throw new Error("The Excel workbook writer is unavailable.");

      const workbook = new Workbook();
      const listingsSheet = workbook.addWorksheet("Listings");
      listingsSheet.columns = LISTING_FIELDS.map((field) => ({
        header: field.label,
        key: field.key,
        width: Math.max(18, field.label.length + 2),
      }));
      listingsSheet.views = [{ state: "frozen", ySplit: 1 }];

      const firstId = (name) => catalogs[name][0].id;
      const firstIds = (name, count = 2) =>
        (catalogs[name] || [])
          .slice(0, count)
          .map((item) => item.id)
          .join(";");
      // One catalog ID per bed/bathroom (duplicates allowed), matching clientside.
      const repeatId = (name, count) =>
        Array.from({ length: count }, () => firstId(name)).join(";");
      const exampleBedsTotal = 2;
      const exampleBathroomsTotal = 2;
      const baseListing = {
        userId: "",
        description: "Example multi-unit property. Replace with accurate details.",
        propertyType: firstId("propertyTypes"),
        roomType: firstId("roomTypes"),
        roomAmount: 1,
        personalHome: true,
        totalUnits: 2,
        guests: 2,
        bedrooms: 1,
        bedsTotal: exampleBedsTotal,
        bedTypes: repeatId("bedTypes", exampleBedsTotal),
        bathroomsTotal: exampleBathroomsTotal,
        bathroomTypes: repeatId("bathroomTypes", exampleBathroomsTotal),
        countryEn: "Saudi Arabia",
        countryAr: "المملكة العربية السعودية",
        street: "Example Street",
        apt: "",
        cityEn: "Jeddah",
        cityAr: "جدة",
        state: "Example district",
        zip: "11111",
        essentials: firstIds("essentials"),
        safeties: firstIds("safeties"),
        spaces: firstIds("spaces"),
        propertyRules: firstIds("propertyRules", 1),
        checkInFrom: "03:00 PM",
        checkInTo: "11:00 AM",
        cancellationPolicy: firstId("cancellationPolicies"),
        currency: "SAR",
        basePrice: 100,
        taxes: 15,
        thursdayPrice: 100,
        fridayPrice: 100,
        saturdayPrice: 100,
        weeklyDiscount: 0,
        monthlyDiscount: 0,
        availabilityWindow: "allFutureDates",
        minNights: 1,
        maxNights: 15,
        wifiName: "Example Wi-Fi",
        wifiPassword: "",
        securityDepositEnabled: false,
        securityDepositAmount: "",
        securityDepositRefundMethod: "cash",
        securityDepositNotes: "",
      };

      // Sample Jeddah map pins — replace with each listing's real coordinates.
      [
        ["512345678", "Example Property 1", "21.4858", "39.1925"],
        ["512345678", "Example Property 2", "21.5433", "39.1728"],
        ["512345677", "Example Property 3", "21.5169", "39.2192"],
        ["512345677", "Example Property 4", "21.3891", "39.8579"],
      ].forEach(([mobileNumber, title, latitudes, longitudes]) => {
        listingsSheet.addRow({
          ...baseListing,
          mobileNumber,
          title,
          latitudes,
          longitudes,
        });
      });
      listingsSheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: LISTING_FIELDS.length },
      };

      const instructions = workbook.addWorksheet("Instructions");
      instructions.addRows([
        ["Bulk listing import example"],
        ["Column headers use plain English labels. Keep those exact header names; do not rename columns."],
        ["One row creates one listing; repeated mobile numbers assign multiple properties to one account."],
        ["If the import reports multiple matching accounts, enter the matching account ID from the error in the optional Account ID column."],
        ["The Options sheet contains enabled catalogue values and IDs from the current environment. Use the ID values in catalogue columns when needed."],
        ["Property type, room type, cancellation policy, bed types, and bathroom types are required. Amenities and property rules may be left blank."],
        ["Bed types must have exactly one entry per Total beds (semicolon-separated, order = Bed 1; Bed 2; …). Same for Bathroom types and Total bathrooms. Duplicates are allowed."],
        ["Example: Total bathrooms = 2 and Bathroom types = privateId;sharedId (or the same ID twice if both bathrooms match)."],
        ["Essentials, safety amenities, spaces, and property rules cells contain semicolon-separated enabled IDs as examples."],
        ["Latitude and longitude are required for every row so the listing can be placed on the map."],
        ["Do not put photos in this workbook. After you upload the Excel file in admin, attach 3–15 photos per listing in the preview UI (max 5 MB and 5000×5000 pixels each)."],
        ["When every listing has photos, Create submits them for approval (same as a host requesting review). They appear under Listing Requests until an admin approves them."],
        ["Replace all example names, descriptions, addresses, coordinates, and prices with the real listing details before importing."],
      ]);
      instructions.getColumn(1).width = 115;
      instructions.getRow(1).font = { bold: true, size: 14 };
      instructions.eachRow((row) => {
        row.getCell(1).alignment = { wrapText: true, vertical: "top" };
      });

      const optionsSheet = workbook.addWorksheet("Options");
      optionsSheet.columns = [
        { header: "Column", key: "field", width: 28 },
        { header: "English name", key: "englishName", width: 30 },
        { header: "Arabic name", key: "arabicName", width: 30 },
        { header: "ID", key: "id", width: 28 },
      ];
      CATALOG_COLUMNS.forEach(([catalogKey, field]) => {
        (catalogs[catalogKey] || []).forEach((item) => {
          optionsSheet.addRow({
            field: LABEL_BY_KEY[field] || field,
            englishName: item.en,
            arabicName: item.ar,
            id: item.id,
          });
        });
      });
      optionsSheet.views = [{ state: "frozen", ySplit: 1 }];
      optionsSheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: Math.max(1, optionsSheet.rowCount), column: 4 },
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const fileUrl = URL.createObjectURL(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const anchor = document.createElement("a");
      anchor.href = fileUrl;
      anchor.download = "bulk-listings-example.xlsx";
      anchor.click();
      URL.revokeObjectURL(fileUrl);
    } catch (error) {
      setParseError(error.message || "Could not create the Excel example.");
    } finally {
      setIsDownloadingExample(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setValidationErrors([]);
    setCreatedListings([]);
    setParseError("");
    setListings([]);
    replacePhotosByRow({});
    setFileName("");
    setWorkbookValid(false);

    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setParseError("Choose an .xlsx workbook. Legacy .xls files are not supported.");
      event.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setParseError("The workbook must be smaller than 5 MB.");
      event.target.value = "";
      return;
    }

    setIsParsing(true);
    try {
      const ExcelJS = await import("exceljs");
      const Workbook = ExcelJS.Workbook || ExcelJS.default?.Workbook;
      if (!Workbook) throw new Error("The Excel workbook reader is unavailable.");

      const workbook = new Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      const worksheet = workbook.getWorksheet("Listings");
      if (!worksheet) {
        throw new Error('The workbook must contain a sheet named "Listings".');
      }

      const headerRow = worksheet.getRow(1);
      const columnByKey = new Map();
      headerRow.eachCell((cell, columnNumber) => {
        const header = String(cellValue(cell.value)).trim();
        if (!header) return;
        const key = KEY_BY_HEADER[header.toLowerCase()];
        if (key) columnByKey.set(key, columnNumber);
      });
      const missingColumns = REQUIRED_LISTING_KEYS.filter(
        (column) => !columnByKey.has(column),
      );
      if (missingColumns.length) {
        throw new Error(
          `Missing required columns: ${missingColumns
            .map((key) => LABEL_BY_KEY[key] || key)
            .join(", ")}`,
        );
      }

      const parsedRows = [];
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const parsed = { rowNumber };
        LISTING_FIELD_KEYS.forEach((column) => {
          const columnNumber = columnByKey.get(column);
          parsed[column] =
            columnNumber === undefined
              ? ""
              : cellValue(row.getCell(columnNumber).value);
        });
        if (!isEmptyRow(parsed)) parsedRows.push(parsed);
      });

      if (!parsedRows.length) throw new Error("The Listings sheet has no property rows.");
      if (parsedRows.length > 100) {
        throw new Error("A workbook can contain at most 100 property rows.");
      }

      setFileName(file.name);
      setListings(parsedRows);
      setIsParsing(false);
      await validateWorkbookRows(parsedRows);
    } catch (error) {
      setParseError(error.message || "Could not read this Excel workbook.");
      event.target.value = "";
      setIsParsing(false);
    }
  };

  const handlePhotosSelected = async (rowNumber, event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;

    setParseError("");
    const existing = photosByRowRef.current[rowNumber] || [];
    const remainingSlots = MAX_PHOTOS - existing.length;
    if (remainingSlots <= 0) {
      toast.error(
        `This listing already has the maximum of ${MAX_PHOTOS} photos.`,
      );
      return;
    }

    if (selected.length > remainingSlots) {
      toast.error(
        `Only ${remainingSlots} more photo${remainingSlots === 1 ? "" : "s"} can be added (max ${MAX_PHOTOS}). Extra files were ignored.`,
      );
    }

    const accepted = [];
    for (const file of selected.slice(0, remainingSlots)) {
      if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
        toast.error(
          `${file.name}: only JPG, PNG, or WebP images are allowed.`,
        );
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        toast.error(`${file.name}: exceeds the 5 MB size.`);
        continue;
      }

      try {
        const { width, height } = await readImageDimensions(file);
        if (width > MAX_PHOTO_DIMENSION || height > MAX_PHOTO_DIMENSION) {
          toast.error(
            `${file.name}: exceeds the maximum resolution of ${MAX_PHOTO_DIMENSION}×${MAX_PHOTO_DIMENSION} pixels (${width}×${height}).`,
          );
          continue;
        }
      } catch (error) {
        toast.error(
          error.message || `${file.name}: could not validate this image.`,
        );
        continue;
      }

      accepted.push({
        id: `${rowNumber}-${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    if (!accepted.length) return;

    setPhotosByRow((prev) => ({
      ...prev,
      [rowNumber]: [...(prev[rowNumber] || []), ...accepted],
    }));
  };

  const removePhoto = (rowNumber, photoId) => {
    setPhotosByRow((prev) => {
      const existing = prev[rowNumber] || [];
      const nextForRow = existing.filter((entry) => {
        if (entry.id !== photoId) return true;
        if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
        return false;
      });
      return { ...prev, [rowNumber]: nextForRow };
    });
  };

  const setCoverPhoto = (rowNumber, photoId) => {
    setPhotosByRow((prev) => {
      const existing = prev[rowNumber] || [];
      const cover = existing.find((entry) => entry.id === photoId);
      if (!cover) return prev;
      return {
        ...prev,
        [rowNumber]: [
          cover,
          ...existing.filter((entry) => entry.id !== photoId),
        ],
      };
    });
  };

  const handleImport = async () => {
    if (!listings.length || isSubmitting || !allPhotosReady) return;
    setIsSubmitting(true);
    setValidationErrors([]);
    setCreatedListings([]);
    setParseError("");

    const linkedTypeErrors = getLinkedTypeCountErrors(listings);
    if (linkedTypeErrors.length) {
      setValidationErrors(linkedTypeErrors);
      toast.error(
        "Bed/bathroom type counts must match Total beds and Total bathrooms.",
      );
      setIsSubmitting(false);
      return;
    }

    try {
      const filesByRow = {};
      listings.forEach((listing) => {
        filesByRow[listing.rowNumber] = (photosByRow[listing.rowNumber] || []).map(
          (entry) => entry.file,
        );
      });

      const response = await submitBulkCreate(listings, filesByRow);
      if (!response.success) {
        setValidationErrors(response.errors || []);
        return;
      }
      setCreatedListings(response.listings || []);
      setListings([]);
      replacePhotosByRow({});
      setFileName("");
      setWorkbookValid(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      const response = error.response?.data;
      if (Array.isArray(response?.errors)) {
        setValidationErrors(response.errors);
        setWorkbookValid(false);
      } else {
        setParseError(
          response?.message || "Import failed. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearSelectedFile = () => {
    setListings([]);
    replacePhotosByRow({});
    setFileName("");
    setValidationErrors([]);
    setCreatedListings([]);
    setParseError("");
    setWorkbookValid(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="page-content">
      <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
        <div className="flex flex-col gap-6 p-6">
          <Link
            to="/property"
            className="flex w-fit items-center gap-2 text-secondaryColor"
          >
            <ArrowLeft size={16} />
            Back to listings
          </Link>

          <div>
            <h1 className="text-2xl dark:text-[#c7d8eb]">
              Add listings from Excel
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Upload one row per property. Repeat the user&apos;s mobile number
              on each row to assign multiple properties to that account. After
              preview, attach photos for each listing, then create them and
              submit for approval.
            </p>
          </div>

          <div className="rounded-md border border-amber-400 bg-amber-50 p-4 text-sm text-amber-900">
            Excel is for listing data only. After you choose the workbook,
            attach {MIN_PHOTOS}–{MAX_PHOTOS} photos per property in the preview
            below. Create stays disabled until every row has photos. Listings
            are then submitted for approval (same as a host request) and appear
            under Listing Requests. Guests in the workbook are promoted to hosts
            when their listings are created.
          </div>

          {catalogError && (
            <div role="alert" className="rounded-md bg-red-50 p-4 text-red-700">
              Could not load the enabled property options needed to create the
              example workbook. {catalogError}
            </div>
          )}

          {catalogs && (
            <details className="rounded-md border border-gray-300 p-4 text-sm dark:border-gray-700">
              <summary className="cursor-pointer font-semibold">
                Enabled Excel lookup values
              </summary>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                {CATALOG_COLUMNS.map(([catalogKey, field]) => (
                  <div key={catalogKey}>
                    <h3 className="font-medium">
                      {LABEL_BY_KEY[field] || field}
                    </h3>
                    {(catalogs[catalogKey] || []).length ? (
                      <ul className="mt-1 list-inside list-disc text-gray-600 dark:text-gray-300">
                        {catalogs[catalogKey].map((item) => (
                          <li key={item.id}>
                            {item.en || item.ar} — {item.id}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-amber-700">
                        No enabled options are configured.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}

          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={downloadExample}
              disabled={!catalogs || isDownloadingExample}
              className="flex items-center gap-2 rounded-md border border-secondaryColor px-4 py-2 text-secondaryColor disabled:opacity-50"
            >
              <FileSpreadsheet size={18} />
              {isDownloadingExample
                ? "Preparing example..."
                : catalogs
                  ? "Download Excel example"
                  : "Loading enabled options..."}
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isParsing || isValidatingWorkbook || isSubmitting}
              className="flex items-center gap-2 rounded-md bg-secondaryColor px-4 py-2 text-white disabled:opacity-50"
            >
              <Upload size={18} />
              {isParsing
                ? "Reading workbook..."
                : isValidatingWorkbook
                  ? "Validating workbook..."
                  : "Choose .xlsx file"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
              className="hidden"
            />
            {fileName && <span className="text-sm">{fileName}</span>}
          </div>

          {parseError && (
            <div role="alert" className="rounded-md bg-red-50 p-4 text-red-700">
              {parseError}
            </div>
          )}

          {listings.length > 0 && (
            <section className="flex flex-col gap-4">
              <h2 className="text-lg font-semibold">
                Preview: {listings.length} listing
                {listings.length === 1 ? "" : "s"}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                The workbook is validated as soon as you upload it. Fix any
                errors below before adding photos. Scroll horizontally to see
                the full workbook.
              </p>
              {isValidatingWorkbook && (
                <div className="rounded-md border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900">
                  Validating workbook values against accounts and enabled
                  options...
                </div>
              )}
              {workbookValid && validationErrors.length === 0 && (
                <div className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800">
                  Workbook validated. Attach {MIN_PHOTOS}–{MAX_PHOTOS} photos
                  for each listing, then create.
                </div>
              )}
              <div className="overflow-x-auto border border-gray-400 dark:border-white">
                <table className="w-max min-w-full border-collapse text-center text-sm">
                  <thead>
                    <tr>
                      <th className="min-w-[90px] border-b border-gray-500 px-2 py-2 font-semibold dark:border-white">
                        Excel row
                      </th>
                      {LISTING_FIELDS.map((field) => (
                        <th
                          key={`preview-header-${field.key}`}
                          className="min-w-[140px] border-b border-gray-500 px-2 py-2 font-semibold dark:border-white"
                        >
                          {field.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((listing) => (
                      <tr key={listing.rowNumber}>
                        <td className="break-words border-b border-gray-500 px-2 py-2 dark:border-white">
                          {listing.rowNumber}
                        </td>
                        {LISTING_FIELDS.map((field) => (
                          <td
                            key={`${listing.rowNumber}-${field.key}`}
                            className="max-w-[220px] break-words border-b border-gray-500 px-2 py-2 dark:border-white"
                          >
                            {listing[field.key] === undefined ||
                            listing[field.key] === null ||
                            String(listing[field.key]).trim() === ""
                              ? "—"
                              : String(listing[field.key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {validationErrors.length > 0 && (
                <section
                  role="alert"
                  className="rounded-md border border-red-300 bg-red-50 p-4 text-red-800"
                >
                  <h2 className="font-semibold">
                    Fix these workbook values and upload the Excel file again
                    before adding photos:
                  </h2>
                  <p className="mt-2">
                    For Mobile number ambiguity, copy one of the account IDs
                    shown in the error into that row&apos;s optional Account ID
                    cell. Use the Options sheet to correct catalogue IDs.
                  </p>
                  <ul className="mt-2 list-inside list-disc">
                    {validationErrors.map((error, index) => (
                      <li key={`${error.rowNumber}-${error.field}-${index}`}>
                        Row {error.rowNumber},{" "}
                        {LABEL_BY_KEY[error.field] || error.field}:{" "}
                        {error.message}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <div className="flex flex-col gap-4">
                <h3 className="text-base font-semibold">Photos per listing</h3>
                {!workbookValid && (
                  <p className="text-sm text-amber-700">
                    Photo upload stays locked until the workbook passes
                    validation.
                  </p>
                )}
                <div className="rounded-md border border-gray-300 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-darkBg2 dark:text-gray-300">
                  <p>{PHOTO_REQUIREMENTS_EN}</p>
                  <p className="mt-2">
                    Accepted formats: JPG, PNG, or WebP. The first photo is the
                    cover. Maximum {MAX_PHOTOS} photos per listing.
                  </p>
                </div>
                {listings.map((listing) => {
                  const photos = photosByRow[listing.rowNumber] || [];
                  const ready =
                    photos.length >= MIN_PHOTOS && photos.length <= MAX_PHOTOS;
                  return (
                    <div
                      key={`photos-${listing.rowNumber}`}
                      className="rounded-md border border-gray-400 p-4 dark:border-white"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">
                            {listing.title || "Untitled"}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">
                            {photos.length} / {MAX_PHOTOS} photos
                            {!ready && (
                              <span className="text-amber-700">
                                {" "}
                                — need at least {MIN_PHOTOS}
                              </span>
                            )}
                            {ready && (
                              <span className="text-green-700"> — ready</span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={
                            !workbookValid ||
                            isValidatingWorkbook ||
                            isSubmitting ||
                            photos.length >= MAX_PHOTOS
                          }
                          onClick={() =>
                            photoInputRefs.current[listing.rowNumber]?.click()
                          }
                          className="flex items-center gap-2 rounded-md border border-secondaryColor px-3 py-2 text-secondaryColor disabled:opacity-50"
                        >
                          <ImagePlus size={16} />
                          Add photos
                        </button>
                        <input
                          ref={(node) => {
                            photoInputRefs.current[listing.rowNumber] = node;
                          }}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          multiple
                          className="hidden"
                          onChange={(event) =>
                            handlePhotosSelected(listing.rowNumber, event)
                          }
                        />
                      </div>
                      {photos.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-3">
                          {photos.map((photo, index) => (
                            <div
                              key={photo.id}
                              className="relative h-24 w-24 overflow-hidden rounded-md border border-gray-300 dark:border-gray-600"
                            >
                              <img
                                src={photo.previewUrl}
                                alt={`Row ${listing.rowNumber} photo ${index + 1}`}
                                className="h-full w-full object-cover"
                              />
                              {index === 0 && (
                                <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[10px] text-white">
                                  Cover
                                </span>
                              )}
                              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 p-1">
                                {index !== 0 && (
                                  <button
                                    type="button"
                                    className="text-[10px] text-white"
                                    onClick={() =>
                                      setCoverPhoto(listing.rowNumber, photo.id)
                                    }
                                  >
                                    Cover
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="ml-auto text-white"
                                  aria-label="Remove photo"
                                  onClick={() =>
                                    removePhoto(listing.rowNumber, photo.id)
                                  }
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-300">
                The server validates the whole workbook and photos before
                creating any listings. Category and amenity values must match
                enabled settings by English/Arabic name or MongoDB ID. latitudes
                and longitudes are required for the map pin.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={isSubmitting || !allPhotosReady}
                  className="rounded-md bg-secondaryColor px-4 py-2 text-white disabled:opacity-50"
                >
                  {isSubmitting
                    ? "Creating listings..."
                    : `Create and request approval for ${listings.length} listing${listings.length === 1 ? "" : "s"}`}
                </button>
                <button
                  type="button"
                  onClick={clearSelectedFile}
                  disabled={isSubmitting}
                  className="rounded-md border border-gray-400 px-4 py-2"
                >
                  Clear file
                </button>
              </div>
              {!workbookValid && !isValidatingWorkbook && (
                <p className="text-sm text-amber-700">
                  Fix workbook errors and re-upload the Excel file before
                  creating.
                </p>
              )}
              {workbookValid && !allPhotosReady && (
                <p className="text-sm text-amber-700">
                  Attach {MIN_PHOTOS}–{MAX_PHOTOS} photos for every listing
                  before creating.
                </p>
              )}
            </section>
          )}

          {createdListings.length > 0 && (
            <section className="rounded-md border border-green-300 bg-green-50 p-4 text-green-800">
              <h2 className="font-semibold">
                Created {createdListings.length} listing
                {createdListings.length === 1 ? "" : "s"} and submitted for
                approval.
              </h2>
              <ul className="mt-2 list-inside list-disc">
                {createdListings.map((listing, index) => (
                  <li key={String(listing.propertyId)}>
                    {index + 1}. {listing.title} (host {listing.mobileNumber},
                    property {String(listing.propertyId)}
                    {listing.photoCount
                      ? `, ${listing.photoCount} photos`
                      : ""}
                    )
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </Container>
    </div>
  );
};

export default BulkCreateListings;
